/**
 * [T149-PR9-FE] AmountHistogramBarSkin — `amount-histogram-bar` adapter.
 *
 * Resolver: amountHistogram — returns 5 stable transaction-amount buckets.
 * Layout:
 *   - isTooSmall  → total count + "transactions" KPI
 *   - isMobile    → bar chart without YAxis labels (more space for bars)
 *   - desktop     → full bar chart with XAxis bucket labels + YAxis count
 *
 * @see https://recharts.org/en-US/api/BarChart
 * @see docs/design/T149-PR5-INSIGHTS-SKINS-SPEC.md §3.10
 */
import { BarChartHorizontal } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useCurrency } from '@/hooks/useCurrency';
import { WidgetFrame } from '../../WidgetFrame';
import type { SkinProps } from '../types';
import {
  skinTestId,
  SkinLoadingBars,
  SkinEmptyState,
  SkinErrorState,
  useSkinFrameProps,
} from './_shared';

const MIN_W = 2;
const MIN_H = 2;

const CHART_CONFIG: ChartConfig = {
  count: { label: 'Transactions', color: 'hsl(var(--chart-1))' },
};

/** Custom tooltip — defined at module scope to prevent re-creation on every render */
function CustomTooltip({
  active,
  payload,
  label,
  fmt,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: { total: number } }>;
  label?: string;
  fmt: (n: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className="text-muted-foreground">
        {entry.value} {entry.value === 1 ? 'transaction' : 'transactions'}
      </p>
      <p className="text-muted-foreground">{fmt(entry.payload.total)} total</p>
    </div>
  );
}

export default function AmountHistogramBarSkin(props: SkinProps<'amount-histogram-bar'>) {
  const { widget, data, loading, error } = props;
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;

  const buckets = data?.buckets ?? [];
  const totalCount = data?.totalCount ?? 0;
  const isEmpty = !loading && !error && totalCount === 0;

  const chartData = buckets.map((b) => ({
    label: b.label,
    count: b.count,
    total: b.total,
  }));

  const frameProps = useSkinFrameProps(props);

  // Too-small fallback: total count KPI
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Transaction Size Distribution'}
        icon={<BarChartHorizontal className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingBars bars={3} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-0.5">
              <span className="text-2xl font-bold tabular-nums">{totalCount}</span>
              <span className="text-[10px] text-muted-foreground">transactions</span>
            </div>
          )
        }
      />
    );
  }

  // Mobile: bar chart without YAxis labels
  if (isMobile) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Transaction Size Distribution'}
        icon={<BarChartHorizontal className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <div data-testid={skinTestId('amount-histogram-bar', widget.id, 'loading')}>
              <SkinLoadingBars bars={5} />
            </div>
          ) : error ? (
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          ) : isEmpty ? (
            <div data-testid={skinTestId('amount-histogram-bar', widget.id, 'empty')}>
              <SkinEmptyState
                title="No transactions in this period"
                description="Add transactions to see the size distribution."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <div
              role="img"
              aria-label={`Transaction size distribution: ${totalCount} transactions`}
              className="w-full h-full min-w-0 min-h-0 overflow-hidden"
              data-testid={skinTestId('amount-histogram-bar', widget.id)}
            >
              <ChartContainer config={CHART_CONFIG} className="h-full w-full overflow-hidden">
                <BarChart data={chartData} margin={{ left: 4, right: 4, top: 4, bottom: 4 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-border/60"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                    interval={0}
                  />
                  <ChartTooltip content={<CustomTooltip fmt={fmt} />} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill="hsl(var(--chart-1))" />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </div>
          )
        }
      />
    );
  }

  // Desktop: full bar chart with XAxis + YAxis
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Transaction Size Distribution'}
      icon={<BarChartHorizontal className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('amount-histogram-bar', widget.id, 'loading')}>
            <SkinLoadingBars bars={5} />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('amount-histogram-bar', widget.id, 'error')}>
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          </div>
        ) : isEmpty ? (
          <div data-testid={skinTestId('amount-histogram-bar', widget.id, 'empty')}>
            <SkinEmptyState
              title="No transactions in this period"
              description="Add transactions to see the size distribution."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Transaction size distribution: ${totalCount} transactions`}
            className="w-full h-full min-w-0 min-h-0 overflow-hidden"
            data-testid={skinTestId('amount-histogram-bar', widget.id)}
          >
            <ChartContainer config={CHART_CONFIG} className="h-full w-full overflow-hidden">
              <BarChart data={chartData} margin={{ left: 4, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  interval={0}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  width={28}
                  allowDecimals={false}
                />
                <ChartTooltip content={<CustomTooltip fmt={fmt} />} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill="hsl(var(--chart-1))" />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        )
      }
    />
  );
}
